// src/components/portal/property/RoomCard.js

import React, { useState, useEffect, useContext } from 'react';
import { OptionsContext } from '../../../contexts/OptionsContext'; // Adjust the path as needed
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusCircleIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid'; // Import necessary icons
import FixtureInputRow from './FixtureInputRow'; // Import the updated FixtureInputRow component

const RoomCard = ({ room, onRoomUpdate }) => {
  const { options, loading: optionsLoading, error: optionsError } = useContext(OptionsContext);

  // Separate state for roomplanFixtures and generalFixtures
  const [localRoom, setLocalRoom] = useState({
    ...room,
    roomplanFixtures: room.roomplanFixtures || [],
    generalFixtures: room.generalFixtures || [],
  });

  const [isExpanded, setIsExpanded] = useState(false); // State to manage collapse/expand
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false); // State to manage image gallery modal

  // Update localRoom when the room prop changes
  useEffect(() => {
    setLocalRoom({
      ...room,
      roomplanFixtures: room.roomplanFixtures || [],
      generalFixtures: room.generalFixtures || [],
    });
  }, [room]);

  // Handler to update any field in the room
  const handleInputChange = (field, value) => {
    const updatedRoom = { ...localRoom, [field]: value };
    setLocalRoom(updatedRoom);
    onRoomUpdate(updatedRoom);
  };

  // Handler functions for fixtures
  const handleFixtureChange = (fixtureType, index, field, value) => {
    const fixtures = [...(localRoom[fixtureType] || [])];
    const fixture = { ...fixtures[index] };

    if (field.startsWith('dimensions.')) {
      const dimensionField = field.split('.')[1];
      fixture.dimensions = { ...fixture.dimensions, [dimensionField]: value };
    } else {
      fixture[field] = value;
      // If category changes, reset subcategory and dimensions if necessary
      if (field === 'category') {
        fixture.subcategory = '';
        const categoryOption = options.fixtureOptions.find((opt) => opt.category === value);
        if (!categoryOption.requiresDimensions) {
          fixture.dimensions = {}; // Clear dimensions if not required
        }

        // Check if fixtureType needs to change based on requiresDimensions
        const newFixtureType = categoryOption.requiresDimensions ? 'roomplanFixtures' : 'generalFixtures';

        if (newFixtureType !== fixtureType) {
          // Remove fixture from current array
          fixtures.splice(index, 1);

          // Add fixture to the new array
          const newFixturesArray = [...(localRoom[newFixtureType] || []), fixture];

          // Update localRoom with changes
          const updatedRoom = {
            ...localRoom,
            [fixtureType]: fixtures,
            [newFixtureType]: newFixturesArray,
          };
          setLocalRoom(updatedRoom);
          onRoomUpdate(updatedRoom);
          return; // Exit the function as we've moved the fixture
        }
      }
    }

    fixtures[index] = fixture;
    const updatedRoom = { ...localRoom, [fixtureType]: fixtures };
    setLocalRoom(updatedRoom);
    onRoomUpdate(updatedRoom);
  };

  const handleAddFixture = () => {
    const newFixture = {
      category: '',
      subcategory: '',
      quantity: 1,
      dimensions: {}, // Initialize empty dimensions
    };
    // Default to generalFixtures; will adjust based on selected category
    const fixtures = [...(localRoom.generalFixtures || []), newFixture];
    const updatedRoom = { ...localRoom, generalFixtures: fixtures };
    setLocalRoom(updatedRoom);
    onRoomUpdate(updatedRoom);
  };

  const handleRemoveFixture = (fixtureType, index) => {
    const fixtures = [...(localRoom[fixtureType] || [])];
    fixtures.splice(index, 1);
    const updatedRoom = { ...localRoom, [fixtureType]: fixtures };
    setLocalRoom(updatedRoom);
    onRoomUpdate(updatedRoom);
  };

  // Function to extract room type from roomID
  const getRoomType = (roomID) => {
    const index = roomID.indexOf('-');
    if (index !== -1) {
      return roomID.substring(0, index);
    }
    return roomID; // If no '-', return the entire string
  };

  // Toggle expand/collapse state
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Toggle image gallery modal
  const toggleImageGallery = () => {
    setIsImageGalleryOpen(!isImageGalleryOpen);
  };

  // Extract and modify image URLs
  const imageUrls =
    localRoom.roomImageValuationObjCollection?.map((imgObj, idx) => ({
      url: imgObj.roomImageURL.replace('/upload/', '/upload/q_auto:low/w_1000/'),
      alt: `Room Image ${idx + 1}`,
    })) || [];

  if (optionsLoading) {
    return <div>Loading options...</div>;
  }

  if (optionsError) {
    return <div>Error loading options: {optionsError}</div>;
  }

  // Ensure options are available
  if (!options.fixtureOptions || !options.floorTypeOptions || !options.wallTypeOptions) {
    return <div>Options not available.</div>;
  }

  // Combine fixtures for rendering, keeping track of their original indices
  const combinedFixtures = [
    ...localRoom.roomplanFixtures.map((fixture, idx) => ({
      ...fixture,
      fixtureType: 'roomplanFixtures',
      originalIndex: idx,
    })),
    ...localRoom.generalFixtures.map((fixture, idx) => ({
      ...fixture,
      fixtureType: 'generalFixtures',
      originalIndex: idx,
    })),
  ];

  return (
    <div className="bg-white shadow-md rounded-lg mb-6 overflow-hidden">
      {/* Header Section */}
      <div
        className="flex justify-between items-center bg-gray-700 p-4 cursor-pointer"
        onClick={toggleExpand}
        aria-expanded={isExpanded}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleExpand();
          }
        }}
      >
        {/* Room Type */}
        <div className="text-lg font-semibold text-white">{getRoomType(localRoom.roomID)}</div>

        {/* Expand/Collapse Icon */}
        <div className="w-6 h-6">
          {isExpanded ? (
            <ChevronUpIcon className="h-6 w-6 text-white" />
          ) : (
            <ChevronDownIcon className="h-6 w-6 text-white" />
          )}
        </div>
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="p-6">
          {/* Images Section */}
          <div className="mb-6">

            {/* Optionally, you can add a thumbnail preview here */}
            {/* ... */}
          </div>

          {/* Floor and Wall Type Selection */}
          <div className="flex flex-wrap -mx-2 mb-4">
            {/* Header with Images Button */}
            <div className="w-full md:w-1/ px-2 mb-4 md:mb-0">
              <div className="flex items-center mb-3">
                <h5 className="text-xl font-semibold mr-2">Room Images</h5>
                {imageUrls.length > 0 ? (
                  <button
                    onClick={toggleImageGallery}
                    className="flex items-center bg-blue-500 text-white text-sm px-2 py-1 rounded-md hover:bg-blue-600 transition-colors"
                  >
                    <PhotoIcon className="h-4 w-4 mr-1" />
                    View Images
                  </button>
                ) : (
                  <span className="text-sm text-gray-500">No images available</span>
                )}
              </div>
            </div>
            {/* Floor Type */}
            <div className="w-full md:w-1/4 px-2 mb-4 md:mb-0">
              <label htmlFor={`floorType-${localRoom.roomID}`} className="block">
                <span className="font-medium">Floor Type:</span>
                <select
                  id={`floorType-${localRoom.roomID}`}
                  value={localRoom.floor?.floorType || ''}
                  onChange={(e) =>
                    handleInputChange('floor', { ...localRoom.floor, floorType: e.target.value })
                  }
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="" disabled>
                    Select Floor Type
                  </option>
                  {options.floorTypeOptions.map((type, idx) => (
                    <option key={`floor-${idx}`} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Wall Type */}
            <div className="w-full md:w-1/4 px-2">
              <label htmlFor={`wallType-${localRoom.roomID}`} className="block">
                <span className="font-medium">Wall Type:</span>
                <select
                  id={`wallType-${localRoom.roomID}`}
                  value={localRoom.wall?.wallType || ''}
                  onChange={(e) =>
                    handleInputChange('wall', { ...localRoom.wall, wallType: e.target.value })
                  }
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="" disabled>
                    Select Wall Type
                  </option>
                  {options.wallTypeOptions.map((type, idx) => (
                    <option key={`wall-${idx}`} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* Fixtures Section */}
          <div className="mb-6">
            {/* Header with Add Button */}
            <div className="flex items-center mb-3">
              <h5 className="text-xl font-semibold mr-2">Fixtures</h5>
              <button
                onClick={handleAddFixture}
                className="flex items-center bg-yellow-500 text-white text-sm px-2 py-1 rounded-md hover:bg-yellow-600 transition-colors"
              >
                <PlusCircleIcon className="h-4 w-4 mr-1" />
                Add Fixture
              </button>
            </div>

            {/* Fixture Inputs Header */}
            <div className="flex flex-wrap -mx-2 mb-2">
              <div className="w-full md:w-3/12 px-2">
                <span className="font-medium text-gray-600">Category</span>
              </div>
              <div className="w-full md:w-4/12 px-2">
                <span className="font-medium text-gray-600">Subcategory</span>
              </div>
              <div className="w-full md:w-1/12 px-2">
                <span className="font-medium text-gray-600">Height(m)</span>
              </div>
              <div className="w-full md:w-1/12 px-2">
                <span className="font-medium text-gray-600">Width(m)</span>
              </div>
              <div className="w-full md:w-1/12 px-2">
                <span className="font-medium text-gray-600">Depth(m)</span>
              </div>
              <div className="w-full md:w-1/12 px-2">
                <span className="font-medium text-gray-600">Quantity</span>
              </div>
              <div className="w-full md:w-1/12 px-2">
              
              </div>
            </div>


            {/* Fixtures List */}
            {combinedFixtures && combinedFixtures.length > 0 ? (
              combinedFixtures.map((fixture, index) => (
                <FixtureInputRow
                  key={`fixture-${index}`}
                  fixture={fixture}
                  options={options.fixtureOptions}
                  handleFixtureChange={handleFixtureChange}
                  handleRemoveFixture={handleRemoveFixture}
                />
              ))
            ) : (
              <p>No Fixtures</p>
            )}
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {isImageGalleryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 md:w-3/4 lg:w-5/6 xl:w-4/5 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-semibold">Room Images</h2>
              <button
                onClick={toggleImageGallery}
                className="text-gray-600 hover:text-gray-800"
                aria-label="Close Gallery"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              {imageUrls.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {imageUrls.map((img, idx) => (
                    <div key={idx} className="flex justify-center">
                      <img
                        src={img.url}
                        alt={img.alt}
                        className="w-full h-auto object-contain rounded-md shadow-sm"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p>No images available.</p>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-4 border-t">
              <button
                onClick={toggleImageGallery}
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomCard;
